import React from "react";
import { PageProps, Link, graphql } from "gatsby";
import Image, { FixedObject } from "gatsby-image";
import styled from "@emotion/styled";
import AccountTreeOutlined from "@material-ui/icons/AccountTreeOutlined";
import EmojiObjectsOutlined from "@material-ui/icons/EmojiObjectsOutlined";
import SpeedOutlined from "@material-ui/icons/SpeedOutlined";
import BarChartOutlined from "@material-ui/icons/BarChartOutlined";
import ArrowDownward from "@material-ui/icons/ArrowDownward";
import ArrowForward from "@material-ui/icons/ArrowForward";

import { Layout } from "~/components/Layout";
import { SEO } from "~/components/SEO";

type Data = {
  site: {
    siteMetadata: {
      title: string;
      author: {
        name: string;
      };
    };
  };
  headshot: {
    childImageSharp: {
      fixed: FixedObject;
    };
  };
};

const Hero = styled.section({
  display: "flex",
  alignItems: "center",
  gap: "2.5rem",
  marginBottom: "4.5rem",
  "@media (max-width: 640px)": {
    flexDirection: "column-reverse",
    alignItems: "flex-start",
    gap: "1.5rem",
    "& > div": {
      width: "100%",
    },
  },
});

const Eyebrow = styled.p({
  marginBottom: "0.5rem",
  fontSize: "0.85rem",
  letterSpacing: "0.04em",
  textTransform: "uppercase",
  color: "var(--textMuted)",
});

const Headline = styled.h1({
  marginTop: 0,
  marginBottom: "1rem",
  fontSize: "clamp(2rem, 5vw, 2.6rem)",
  lineHeight: 1.15,
});

const Intro = styled.p({
  fontSize: "1.15rem",
  color: "var(--textMuted)",
});

const Actions = styled.div({
  display: "flex",
  flexWrap: "wrap",
  gap: "1rem",
});

const buttonStyles = {
  display: "inline-flex",
  alignItems: "center",
  gap: "0.4em",
  padding: "0.6rem 1rem",
  borderRadius: 6,
  color: "var(--textTitle)",
  textDecoration: "none",
  border: "1px solid var(--border)",
  transition: "border-color 0.2s",
  "&:hover": {
    borderColor: "var(--textMuted)",
  },
  "& svg": {
    fontSize: "1em",
  },
};

const PrimaryButton = styled.a({
  ...buttonStyles,
  background: "var(--surface)",
  borderColor: "transparent",
});

const SecondaryButton = styled(Link)(buttonStyles);

const Headshot = styled(Image)({
  flexShrink: 0,
  borderRadius: "50%",
  background: "var(--avatarBg)",
});

const SectionTitle = styled.h2({
  marginTop: 0,
  marginBottom: "2rem",
  fontSize: "1.6rem",
  fontWeight: 500,
});

const WorkGrid = styled.div({
  display: "grid",
  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
  gap: 16,
  "@media (max-width: 768px)": {
    gridTemplateColumns: "1fr",
  },
});

const WorkItem = styled.article({
  padding: 24,
  background: "var(--cardBg)",
  border: "1px solid var(--cardBorder)",
  borderRadius: 12,
  boxShadow: "0 2px 8px rgba(0, 0, 0, 0.025)",
  "& svg": {
    color: "var(--textMuted)",
    marginBottom: "0.75rem",
  },
  "& h3": {
    marginTop: 0,
    marginBottom: "0.4rem",
    fontSize: "1.4rem",
  },
});

const Tech = styled.p({
  marginBottom: "0.75rem",
  fontSize: "0.85rem",
  fontWeight: 600,
  color: "var(--textMuted)",
});

const Description = styled.p({
  marginBottom: 0,
  fontSize: "0.95rem",
});


const selectedWork = [
  {
    Icon: AccountTreeOutlined,
    title: "Interactive Workflow Builder",
    tech: "React · TypeScript · React Flow",
    description:
      "Led the frontend architecture and development of a visual automation builder at Kajabi, allowing users to create complex workflows with branching logic and dynamic state transitions. Focused on building reusable components and managing complex application state while keeping the interface intuitive and maintainable.",
  },
  {
    Icon: EmojiObjectsOutlined,
    title: "AI-Powered Knowledge Assistant",
    tech: "React · Ruby on Rails · Pinecone · RAG · WebSockets",
    description:
      "Built an AI-powered assistant that lets users ask questions and receive contextual answers based on their course content. Developed the system using retrieval-augmented generation (RAG), vector search, and streaming APIs to deliver responses in real time.",
  },
  {
    Icon: BarChartOutlined,
    title: "Data Visualization & Interactive Dashboards",
    tech: "React · Next.js · Nivo · JavaScript",
    description:
      "Built interactive dashboards and data visualization interfaces at QOS Networks, making complex datasets easier to explore and understand. Focused on performance, usability, and creating intuitive experiences through interactive components and animations.",
  },
  {
    Icon: SpeedOutlined,
    title: "Performance & Frontend Optimization",
    tech: "JavaScript · React · TypeScript · Frontend Architecture",
    description:
      "Led performance improvements for iHerb's global eCommerce platform, reducing page load times by up to 2 seconds in the U.S. and 10 seconds internationally. Also led a large-scale migration to TypeScript at Kajabi, improving code quality, developer productivity, and long-term maintainability.",
  },
];


const Home = ({ data, location }: PageProps<Data>) => {
  const { title, author } = data.site.siteMetadata;

  return (
    <Layout location={location} title={title}>
      <SEO title="Senior Software Engineer" />
      <Hero>
        <div>
          <Eyebrow>Senior Software Engineer</Eyebrow>
          <Headline>Hi, I'm Mark. I build things.</Headline>
          <Intro>
            React, TypeScript, and full-stack development. 20+ years turning
            complex problems into intuitive, maintainable products. I enjoy
            working closely with design and product teams, taking ownership of
            features, and making both the user and developer experience better.
          </Intro>
          <Actions>
            <PrimaryButton href="#work">
              View my work <ArrowDownward />
            </PrimaryButton>
            <SecondaryButton to="/contact">
              Get in touch <ArrowForward />
            </SecondaryButton>
          </Actions>
        </div>
        <Headshot
          fixed={data.headshot.childImageSharp.fixed}
          alt={author.name}
        />
      </Hero>

      <section id="work">
        <SectionTitle>Selected Work</SectionTitle>
        <WorkGrid>
          {selectedWork.map(({ Icon, title, tech, description }) => (
            <WorkItem key={title}>
              <Icon />
              <h3>{title}</h3>
              <Tech>{tech}</Tech>
              <Description>{description}</Description>
            </WorkItem>
          ))}
        </WorkGrid>
      </section>
    </Layout>
  );
};

export default Home;

export const pageQuery = graphql`
  query {
    site {
      siteMetadata {
        title
        author {
          name
        }
      }
    }
    headshot: file(absolutePath: { regex: "/headshot-transparent.png/" }) {
      childImageSharp {
        fixed(width: 160, height: 160, cropFocus: NORTH) {
          ...GatsbyImageSharpFixed
        }
      }
    }
  }
`;
